import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { newsApi, type NewsArticle } from '@/services/api';

function renderMarkdownContent(content: string) {
  const blocks = content.split('\n\n').filter(Boolean);
  return blocks.map((block, idx) => {
    if (block.startsWith('## ')) {
      return (
        <h2 key={idx} className="font-heading text-xl font-bold text-foreground-900 mt-10 mb-4">
          {block.replace('## ', '')}
        </h2>
      );
    }
    if (block.startsWith('### ')) {
      return (
        <h3 key={idx} className="font-heading text-lg font-bold text-foreground-900 mt-8 mb-3">
          {block.replace('### ', '')}
        </h3>
      );
    }
    if (block.includes('\n- ')) {
      const lines = block.split('\n');
      const title = !lines[0].startsWith('- ') ? lines[0] : null;
      const items = lines.filter((l) => l.startsWith('- ')).map((l) => l.replace('- ', ''));
      return (
        <div key={idx} className={title ? 'mt-5' : 'mt-4'}>
          {title && <p className="text-foreground-700 text-sm leading-relaxed mb-2">{title}</p>}
          <ul className="space-y-1.5">
            {items.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-foreground-600 text-sm leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0"></span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    if (block.match(/^\d+\.\s/)) {
      const items = block.split('\n').filter((l) => /^\d+\.\s/.test(l));
      return (
        <ol key={idx} className="space-y-2 mt-4 list-decimal list-inside">
          {items.map((item, i) => (
            <li key={i} className="text-foreground-600 text-sm leading-relaxed">
              {item.replace(/^\d+\.\s/, '')}
            </li>
          ))}
        </ol>
      );
    }
    if (block.includes('|') && block.includes('---')) {
      const lines = block.split('\n').filter((l) => l.includes('|'));
      const headers = lines[0]?.split('|').filter(Boolean).map((c) => c.trim());
      const rows = lines.slice(2).map((l) => l.split('|').filter(Boolean).map((c) => c.trim()));
      if (!headers || rows.length === 0) return null;
      return (
        <div key={idx} className="mt-5 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-background-200">
                {headers.map((h, i) => (
                  <th key={i} className="text-left py-2.5 px-4 text-foreground-900 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                  {row.map((cell, ci) => (
                    <td key={ci} className="py-2.5 px-4 text-foreground-600">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <p key={idx} className="text-foreground-600 text-sm leading-relaxed mt-4">
        {block}
      </p>
    );
  });
}

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    newsApi
      .detail(Number(id))
      .then((data) => {
        setArticle(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '加载失败');
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="w-full bg-background-50 min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="w-full bg-background-50 min-h-screen flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground-900 mt-4">文章不存在</h2>
          <p className="text-foreground-500 text-sm mt-2">{error || '该文章可能已被下架或删除'}</p>
          <Link
            to="/news"
            className="inline-block mt-6 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="w-full bg-background-50 min-h-screen">
        {/* Breadcrumb */}
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <div className="flex items-center gap-2 text-sm text-foreground-400">
              <Link to="/" className="hover:text-foreground-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/news" className="hover:text-foreground-600 transition-colors cursor-pointer">行业资讯</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600 truncate max-w-[200px]">{article.title}</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Main Content */}
            <article className="lg:col-span-8">
              {/* Header */}
              <div className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded">
                    {article.category}
                  </span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 bg-background-100 text-foreground-500 text-xs rounded">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-400">
                  <span className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-3-line text-secondary-600 text-xs"></i>
                    </div>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line text-xs"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-xs"></i>
                    {article.readCount.toLocaleString()} 阅读
                  </span>
                </div>
              </div>

              {/* Cover Image */}
              <div className="w-full h-[280px] md:h-[380px] rounded-xl overflow-hidden mb-10">
                <img
                  src="/images/news-default.jpg"
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Content */}
              <div className="prose-custom">
                {renderMarkdownContent(article.content)}
              </div>

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mt-12 pt-8 border-t border-background-200">
                <span className="text-foreground-400 text-xs">标签：</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/news?tag=${tag}`}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full hover:bg-secondary-100 hover:text-secondary-700 transition-colors cursor-pointer"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-8">
                {/* Energy System CTA */}
                <div className="bg-foreground-900 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-primary-500/20 flex items-center justify-center mb-4">
                    <i className="ri-flashlight-line text-xl text-primary-400"></i>
                  </div>
                  <h4 className="font-heading text-base font-bold text-background-50 mb-2">
                    能耗管理系统
                  </h4>
                  <p className="text-foreground-400 text-xs leading-relaxed mb-5">
                    实时监测 · 智能分析 · 碳排核算
                    <br />
                    500+企业验证，平均节能15-30%
                  </p>
                  <a
                    href="https://ems.firmai.cn/web"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    预约免费演示
                  </a>
                </div>

                {/* Hot Tags */}
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-3">
                    热门标签
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {['能耗管理', '绿证政策', '双碳', '节能改造', '碳市场', '案例分析'].map((tag) => (
                      <Link
                        key={tag}
                        to={`/news?tag=${tag}`}
                        className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full hover:bg-secondary-100 hover:text-secondary-700 transition-colors cursor-pointer"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}